<script setup lang="ts">
import PhotonCalibrationVisualizer from "@/components/app/photon-calibration-visualizer.vue";
import type { CameraCalibrationResult, VideoFormat } from "@/types/SettingTypes";
import { WebsocketPipelineType } from "@/types/WebsocketDataTypes";
import { useCameraSettingsStore } from "@/stores/settings/CameraSettingsStore";
import { useStateStore } from "@/stores/StateStore";
import { computed, inject, ref, useTemplateRef, watch } from "vue";
import axios from "axios";
import {
  axiosPost,
  getCalibrationSummaryStatistics,
  getResolutionString,
  meanReprojectionError,
  parseJsonFile
} from "@/lib/PhotonUtils";
import { useTheme } from "vuetify";
import PvDeleteModal from "@/components/common/pv-delete-modal.vue";
import PhotonUncertaintyVisualizer from "@/components/app/photon-uncertainty-visualizer.vue";

const theme = useTheme();
const props = defineProps<{
  videoFormat: VideoFormat;
}>();

// While a calibration session is active this card only ever shows the pending calibration
const isCalibrating = computed(
  () => useCameraSettingsStore().currentCameraSettings.currentPipelineIndex === WebsocketPipelineType.Calib3d.valueOf()
);

// The full calibration (with observations) for this resolution.
const fetchedCalibration = ref<CameraCalibrationResult | null>(null);

const fetchCalibration = async () => {
  try {
    const response = await axios.get("/settings/camera/getCalibration", {
      params: {
        cameraUniqueName: useCameraSettingsStore().currentCameraSettings.uniqueName,
        width: props.videoFormat.resolution.width,
        height: props.videoFormat.resolution.height
      }
    });
    fetchedCalibration.value = response.data;
  } catch {
    fetchedCalibration.value = null;
  }
};

watch(() => props.videoFormat.resolution, fetchCalibration, { immediate: true });

const confirmRemoveDialog = ref({ show: false, vf: props.videoFormat });

const removeCalibration = async (vf: VideoFormat) => {
  await axiosPost("/calibration/remove", "delete a camera calibration", {
    cameraUniqueName: useCameraSettingsStore().currentCameraSettings.uniqueName,
    width: vf.resolution.width,
    height: vf.resolution.height
  });
};

const exportCalibration = useTemplateRef("exportCalibration");
const openExportCalibrationPrompt = () => {
  exportCalibration.value?.click();
};

const importCalibrationFromPhotonJson = useTemplateRef("importCalibrationFromPhotonJson");
const openUploadPhotonCalibJsonPrompt = () => {
  importCalibrationFromPhotonJson.value?.click();
};
const importCalibration = async () => {
  const files = importCalibrationFromPhotonJson.value?.files;
  if (!files?.length) return;
  const uploadedJson = files[0];

  const data = await parseJsonFile<CameraCalibrationResult>(uploadedJson);

  if (
    data.resolution.height !== props.videoFormat.resolution.height ||
    data.resolution.width !== props.videoFormat.resolution.width
  ) {
    useStateStore().showSnackbarMessage({
      color: "error",
      message: `The resolution of the calibration export doesn't match the current resolution ${props.videoFormat.resolution.height}x${props.videoFormat.resolution.width}`
    });
    return;
  }

  useCameraSettingsStore()
    .importCalibrationFromData({ calibration: data })
    .then(async (response) => {
      useStateStore().showSnackbarMessage({
        color: "success",
        message: response.data.text || response.data
      });
      // We need to refetch the calibration since the cache will be outdated
      await fetchCalibration();
    })
    .catch((error) => {
      if (error.response) {
        useStateStore().showSnackbarMessage({
          color: "error",
          message: error.response.data.text || error.response.data
        });
      } else if (error.request) {
        useStateStore().showSnackbarMessage({
          color: "error",
          message: "Error while trying to process the request! The backend didn't respond."
        });
      } else {
        useStateStore().showSnackbarMessage({
          color: "error",
          message: "An error occurred while trying to process the request."
        });
      }
    });
};

interface ObservationDetails {
  index: number;
  mean: number;
  numOutliers: number;
  numMissing: number;
}

const currentCalibrationCoeffs = computed<CameraCalibrationResult | undefined>(
  () => fetchedCalibration.value ?? useCameraSettingsStore().getCalibrationCoeffs(props.videoFormat.resolution)
);

const getObservationDetails = (): ObservationDetails[] | undefined => {
  const coefficients = currentCalibrationCoeffs.value;
  if (!coefficients) return undefined;

  // Re-derive stats if they're missing
  if (coefficients.meanErrors !== undefined) {
    return coefficients.meanErrors.map((m, i) => ({
      index: i,
      mean: parseFloat(m.toFixed(2)),
      numOutliers: coefficients.numOutliers[i],
      numMissing: coefficients.numMissing[i]
    }));
  }

  return coefficients.observations?.map((obs, i) => {
    const numMissing = obs.locationInImageSpace.filter((pt) => pt.x < 0 || pt.y < 0).length;
    return {
      index: i,
      mean: parseFloat(meanReprojectionError(obs).toFixed(2)),
      // Mirrors UICameraCalibrationCoefficients -- outliers exclude corners that were never detected
      numOutliers: obs.cornersUsed.filter((used) => !used).length - numMissing,
      numMissing
    };
  });
};

const summaryStatistics = computed(() => {
  const c = currentCalibrationCoeffs.value;
  return c ? getCalibrationSummaryStatistics(c) : undefined;
});
const formatStat = (value: number | undefined, unit: string): string =>
  value !== undefined ? (isNaN(value) ? "NaN" : value.toFixed(2) + unit) : "-";

const exportCalibrationURL = computed<string>(() =>
  useCameraSettingsStore().getCalJSONUrl(inject("backendHost") as string, props.videoFormat.resolution)
);
const calibrationImageURL = (index: number) =>
  useCameraSettingsStore().getCalImageUrl(inject<string>("backendHost") as string, props.videoFormat.resolution, index);

const tab = ref("details");
const viewingImg = ref(0);
</script>

<template>
  <v-card color="surface" dark>
    <v-card-title class="pb-2">
      <div class="d-flex flex-wrap">
        <v-col cols="12" md="6" class="pa-0">
          <v-card-title class="pa-0"> Calibration Details </v-card-title>
        </v-col>
        <v-col v-if="!isCalibrating" cols="6" md="3" class="d-flex align-center pt-0 pb-0 pl-0">
          <v-btn
            color="buttonPassive"
            style="width: 100%"
            :variant="theme.global.current.value.dark ? 'outlined' : 'elevated'"
            @click="openUploadPhotonCalibJsonPrompt"
          >
            <v-icon start size="large"> mdi-import</v-icon>
            <span>Import</span>
          </v-btn>
          <input
            ref="importCalibrationFromPhotonJson"
            type="file"
            accept=".json"
            style="display: none"
            @change="importCalibration"
          />
        </v-col>
        <v-col v-if="!isCalibrating" cols="6" md="3" class="d-flex align-center pt-0 pb-0 pr-0">
          <v-btn
            color="buttonPassive"
            :disabled="!currentCalibrationCoeffs"
            style="width: 100%"
            :variant="theme.global.current.value.dark ? 'outlined' : 'elevated'"
            @click="openExportCalibrationPrompt"
          >
            <v-icon start size="large">mdi-export</v-icon>
            <span>Export</span>
          </v-btn>
          <a
            ref="exportCalibration"
            style="color: black; text-decoration: none; display: none"
            :href="exportCalibrationURL"
            target="_blank"
          />
        </v-col>
      </div>
    </v-card-title>

    <v-card-text class="d-flex flex-row pt-0">
      <v-col cols="4" class="pa-0">
        <v-tabs v-model="tab" grow bg-color="surface" height="48" slider-color="buttonActive">
          <v-tab key="details" value="details">Details</v-tab>
          <v-tab key="observations" value="observations">Observations</v-tab>
          <v-tab key="uncertainty" value="uncertainty">Uncertainty</v-tab>
        </v-tabs>
        <v-tabs-window v-model="tab" class="pt-3">
          <v-tabs-window-item key="details" value="details">
            <v-table style="width: 100%" density="compact">
              <template #default>
                <tbody>
                  <tr>
                    <td>Camera</td>
                    <td>
                      {{ useCameraSettingsStore().currentCameraName }}
                    </td>
                  </tr>
                  <tr>
                    <td>Resolution</td>
                    <td>
                      {{ getResolutionString(videoFormat.resolution) }}
                    </td>
                  </tr>
                  <tr>
                    <td>Fx</td>
                    <td>
                      {{ currentCalibrationCoeffs?.cameraIntrinsics.data[0].toFixed(2) || 0.0 }}
                      px
                    </td>
                  </tr>
                  <tr>
                    <td>Fy</td>
                    <td>
                      {{ currentCalibrationCoeffs?.cameraIntrinsics.data[4].toFixed(2) || 0.0 }}
                      px
                    </td>
                  </tr>
                  <tr>
                    <td>Cx</td>
                    <td>
                      {{ currentCalibrationCoeffs?.cameraIntrinsics.data[2].toFixed(2) || 0.0 }}
                      px
                    </td>
                  </tr>
                  <tr>
                    <td>Cy</td>
                    <td>
                      {{ currentCalibrationCoeffs?.cameraIntrinsics.data[5].toFixed(2) || 0.0 }}
                      px
                    </td>
                  </tr>
                  <tr>
                    <td>Distortion</td>
                    <td>
                      {{ currentCalibrationCoeffs?.distCoeffs.data.map((it) => parseFloat(it.toFixed(3))) || [] }}
                    </td>
                  </tr>
                  <tr>
                    <td>Mean Err</td>
                    <td>{{ formatStat(summaryStatistics?.mean, "px") }}</td>
                  </tr>
                  <tr>
                    <td>Horizontal FOV</td>
                    <td>{{ formatStat(summaryStatistics?.horizontalFOV, "°") }}</td>
                  </tr>
                  <tr>
                    <td>Vertical FOV</td>
                    <td>{{ formatStat(summaryStatistics?.verticalFOV, "°") }}</td>
                  </tr>
                  <tr>
                    <td>Diagonal FOV</td>
                    <td>{{ formatStat(summaryStatistics?.diagonalFOV, "°") }}</td>
                  </tr>
                  <!-- Board warp, only shown for mrcal-calibrated cameras -->
                  <tr v-if="currentCalibrationCoeffs?.calobjectWarp?.length === 2">
                    <td>Board warp, X/Y</td>
                    <td>
                      {{
                        currentCalibrationCoeffs?.calobjectWarp?.map((it) => (it * 1000).toFixed(2) + " mm").join(" / ")
                      }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-table>
          </v-tabs-window-item>
          <v-tabs-window-item key="observations" value="observations">
            <v-data-table
              id="observations-table"
              items-per-page-text="Page size:"
              density="compact"
              style="width: 100%"
              :headers="[
                { title: 'Id', key: 'index' },
                { title: 'Mean Reprojection Error', key: 'mean' }
              ]"
              :items="getObservationDetails()"
              item-value="index"
              show-expand
            >
              <template #item.data-table-expand="{ internalItem }">
                <v-btn
                  class="text-none"
                  size="small"
                  variant="text"
                  slim
                  rounded
                  @click="viewingImg = internalItem.index"
                >
                  <v-icon
                    size="large"
                    :color="viewingImg === internalItem.index ? 'buttonActive' : 'rgba(255, 255, 255, 0.7)'"
                    >mdi-eye</v-icon
                  >
                </v-btn>
              </template>
            </v-data-table>
          </v-tabs-window-item>
          <v-tabs-window-item key="uncertainty" value="uncertainty">
            <p>Calibration uncertainty, in pixels, looking out to infinity. Lower numbers are better.</p>
            <p>
              Uncertainty measures how sure we are about the pixel location that a ray entering our camera would fall
              onto. Lower numbers imply higher certainty. To decrease uncertainty, capture more varied pictures across
              the full field of view of your camera.
            </p>
            <p>
              A lower uncertainty doesn't necesarrily mean that the calibration is more accurate -- just that our solver
              had more information that seemed to correspond. Confounding factors (like incorrect square size) can still
              lead to inaccurate results.
            </p>
            <p>
              For more information, review
              <a href="https://mrcal.secretsauce.net/uncertainty.html">Mrcal's uncertainty documentation</a>
            </p>
          </v-tabs-window-item>
        </v-tabs-window>
      </v-col>
      <v-col cols="8" class="pa-0 pl-6">
        <v-card-text class="pa-0 fill-height d-flex justify-center align-center">
          <div v-if="!currentCalibrationCoeffs">
            <v-alert
              class="pt-3 pb-3"
              color="primary"
              text="The selected video format has not been calibrated."
              icon="mdi-alert-circle-outline"
              :variant="theme.global.current.value.dark ? 'tonal' : 'elevated'"
            />
          </div>
          <Suspense v-else-if="tab === 'details'">
            <!-- Allows us to import three js when it's actually needed  -->
            <PhotonCalibrationVisualizer
              :camera-unique-name="useCameraSettingsStore().currentCameraSettings.uniqueName"
              :resolution="props.videoFormat.resolution"
              title="Camera to Board Transforms"
            />
            <template #fallback>Loading...</template>
          </Suspense>
          <!-- <Suspense v-else-if="tab === 'uncertainty'" style="display: flex; justify-content: center; width: 100%"> -->
          <PhotonUncertaintyVisualizer
            v-else-if="tab === 'uncertainty'"
            :camera-unique-name="useCameraSettingsStore().currentCameraSettings.uniqueName"
            :resolution="props.videoFormat.resolution"
            title="Camera reprojection uncertainty"
          />
          <!-- <template #fallback>Loading...</template> -->
          <!-- </Suspense> -->
          <div v-else style="display: flex; justify-content: center; width: 100%">
            <img :src="calibrationImageURL(viewingImg)" alt="observation image" class="snapshot-preview pt-2 pb-2" />
          </div>
        </v-card-text>
      </v-col>
    </v-card-text>
  </v-card>

  <pv-delete-modal
    v-model="confirmRemoveDialog.show"
    :width="500"
    title="Delete Calibration"
    :description="`Are you sure you want to delete the calibration for '${confirmRemoveDialog.vf.resolution.width}x${confirmRemoveDialog.vf.resolution.height}'? This action cannot be undone.`"
    :on-confirm="() => removeCalibration(confirmRemoveDialog.vf)"
  />
</template>

<style scoped>
.snapshot-preview {
  max-width: 100%;
  max-height: 100%;
}
</style>
